import { Connector, EventData, EventOptions, PaidBooking, PriceData, Snapshot, UnseatedTicket } from "@ticketmachine/sdk";
import client, { replaceURLParams } from "@/utils/http";
import { Readable } from "svelte/store";
import { URLS } from "./urls";


export interface RawPriceData {
    id: number,
    evName: string,
    evDate: string,
    categories: {
        id: number,
        ecName: string,
        deleted: boolean,
        price: number,
        discounts: {id: number, ecdName: string, price: number}[],
        contingent: { all: number },
        showDefaultPrice: boolean
    }[];
}
const runningRequests: Map<string, Promise<any>> = new Map();

export interface RawEventData {
    id: number;
    evName: string;
    badge: string;
    entrytime: string;
    evDate: string;
    endtime: string;
    evDescription: string;
    tags: string[];
    seatingChart: {}|null;
    state: {
        pricesShown: boolean,
        saleActive: boolean
    }
}

export interface RawEventOptions {
    p: number;
    ps: number;

    name: string;
    search: string;
    from: string;
    to: string;
    tag: string;

    id: number;
}


function deduplicateByEvent<T>(type: string, event_id: number, cb: () => Promise<T>): Promise<T> {
    let key = `${type}->${event_id}`;
    if (!runningRequests.has(key)) {
        runningRequests.set(key, (async () => {
            try {
                return await cb();
            } finally {
                runningRequests.delete(key);
                cb = null as any;
                key = null as any;
            }
        })());
    }
    return runningRequests.get(key)! as Promise<T>;
}


export function getPriceData(event_id: number, urls: URLS): Promise<RawPriceData> {
    return deduplicateByEvent("price_data", event_id, async () => {
        return <RawPriceData>(await client.get(replaceURLParams(urls.PriceDataURL, {id: event_id}))).data;
    });
}

export async function getContingent(event_id: number, urls: URLS): Promise<Map<number, {free: number, all: number}>> {
    const cats = await deduplicateByEvent("contingent", event_id, async () => {
        return <{categories: {id: number, contingent: {free: number, all: number}}[]}>(await client.get(replaceURLParams(urls.ContingentURL, {id: event_id}))).data;
    });

    return new Map(cats.categories.map(c => [c.id, c.contingent]));
}


interface ResolverFunctions {
    getEvent: Connector["getEvent"],
    priceFor: Connector["priceFor"],
    getEventImageUrl: Connector["getEventImageUrl"],
    freeTicketsOfEvent: Connector["freeTicketsOfEvent"],
    listEvents: Connector["listEvents"],
    listBundles: Connector["listBundles"],
    loadBundleChecker: Connector["loadBundleChecker"],
    getPaidBooking: Connector["getPaidBooking"]
}

type CachedEventData = (event_id: number) => Promise<RawPriceData>

export default function createResolverConnector(snapshot: Readable<Snapshot>, updateCart: () => Promise<void>, urls: URLS): [CachedEventData, ResolverFunctions] {
    const eventCache: Map<string, Promise<any>> = new Map();
    function pushCache(name: string, value: any) {
        eventCache.set(name, Promise.resolve(value));
    }

    async function cached<T>(name: string, func: () => Promise<T>): Promise<T> {
        if (!eventCache.has(name)) {
            eventCache.set(name, func());
        }
        return await (eventCache.get(name)!);
    }
    const getPriceDataCached = async (event_id: number): Promise<RawPriceData> => {
        return await cached(`event:${event_id}`, async () => {
            return await getPriceData(event_id, urls);
        })
    }

    return [getPriceDataCached, {

        async listBundles(options) {
            return await cached(`listBundles:${JSON.stringify(options)}`, async () => {
                return (await client.get(urls.ListPublicRebatesURL, {params: options})).data;
            });
        },

        async loadBundleChecker(key: string) {
            return await cached(`bundleCheck:${key}`, async () => {
                const settings = (await client.get(urls.CheckSettingsURL, {params: {idx: key}})).data;
                if (settings === null) {
                    return null;
                }
                const wasm_url = new URL(urls.CheckEngineURL, window.location.origin);
                const params = new URLSearchParams();
                params.set("idx", key);
                wasm_url.search = params.toString();
                return {
                    settings,
                    wasm_url: wasm_url.toString()
                }
            });
        },

        async getEvent(event_id: number): Promise<PriceData> {
            const event = await getPriceDataCached(event_id);
            return {
                ...event,
                evDate: new Date(event.evDate),
            }
        },

        async priceFor(ticket: UnseatedTicket): Promise<number> {
            const data = await getPriceDataCached(ticket.event);
            const cat = data.categories.find(c => c.id === ticket.category);
            if (!cat) throw new Error("Couldn't find category");
            if (!ticket.discount) return cat.price;
            const disc = cat.discounts.find(f => f.id === ticket.discount);
            if (!disc) throw new Error("Couldn't find discount");
            return disc.price;
        },


        async getEventImageUrl(event: number, width: number, height: number, gravity: string) {
            const search = new URLSearchParams();
            search.set("width", `${width}`);
            search.set("height", `${height}`);
            search.set("gravity", gravity);
            const url = new URL(replaceURLParams(urls.EventImageURL, {id: event}), window.location.origin);
            url.search = search.toString();
            return url.toString();
        },

        async freeTicketsOfEvent(event_id: number, category_id: number): Promise<number> {
            const rawData = await getContingent(event_id, urls);
            return rawData.get(category_id)?.free || 0;
        },

        async listEvents(options: Partial<EventOptions>): Promise<EventData[]> {
            const params: Partial<RawEventOptions> = {};
            if (options.id !== undefined) params.id =  options.id;
            if (options.name !== undefined) params.name =  options.name;
            if (options.search !== undefined) params.search =  options.search;
            if (options.from !== undefined) params.from =  options.from.toISOString();
            if (options.to !== undefined) params.to =  options.to.toISOString();
            if (options.tag !== undefined) params.tag =  options.tag;
            if (options.page !== undefined) params.p = options.page;
            if (options.page_size !== undefined) params.ps = options.page_size;

            const key = 
                options.id !== undefined
                ? `event.data:${options.id}`
                : `eventList:${JSON.stringify(params)}`;

            return await cached(key, async () => {
                const rawEvent = await client.get<RawEventData[]>(urls.ListEventsURL, {params});

                const mapped = rawEvent.data.map(re => ({
                    id: re.id,
                    name: re.evName,
                    description: re.evDescription,
                    badge: re.badge,
                    entrytime: new Date(re.entrytime),
                    starttime: new Date(re.evDate),
                    endtime: new Date(re.endtime),
                    tags: re.tags,
                    seated: re.seatingChart !== null,
                    state: re.state
                }));

                console.log(mapped);

                if (options.id !== null) {
                    for (let ev of mapped) {
                        pushCache(`event.data:${ev.id}`, [ev]);
                    }
                }

                return mapped;
            })
        },

        async getPaidBooking(id: string): Promise<PaidBooking> {
            return {
                id,
                ticket_url: new URL(replaceURLParams(urls.TicketURL, {uuid: id}), window.location.origin).toString(),
                receipt_url: new URL(replaceURLParams(urls.ReceiptURL, {uuid: id}), window.location.origin).toString()
            }
        }
    }];
}

